import { useFormWithStateContext } from 'contexts/FormWithStateContext';
import { CurrencyCode } from 'further-types/lp/vehicle';
import api from 'lib/trpcClient';
import { round, sumBy } from 'lodash';
import React, { useEffect } from 'react';
import {
  CapitalActionFormState,
  UpdateCapitalActionFormValues,
} from '../../hooks/useCapitalActionForm';
import CapitalContributionCallAllocations from './CapitalContributionCallAllocations';

type Props = {
  vehicleId: string;
  index: number;
  baseCurrency: CurrencyCode;
};

const CapitalContributionCallDetails: React.FC<Props> = ({
  index,
  vehicleId,
  baseCurrency,
}) => {
  const {
    formMethods: { watch, setValue },
  } = useFormWithStateContext<
    UpdateCapitalActionFormValues,
    CapitalActionFormState
  >();

  const [baseAllocations] =
    api.capitalAction.getCommitmentsWithCapitalContributionBalances.useSuspenseQuery(
      {
        vehicleId,
      },
    );

  const capitalBreakdown = watch(`entries.${index}.capitalBreakdown`);

  useEffect(() => {
    if (!capitalBreakdown?.length) {
      const initialBreakdown = baseAllocations.map((a) => ({
        commitmentId: a.commitmentId,
        fundsCalled: {
          fromCommitmentLcy: a.fundsCalled.fromCommitmentLcy,
          fromRecallableDistributionLcy: 0,
        },
      }));
      setValue(`entries.${index}.capitalBreakdown`, initialBreakdown);
      setValue(
        `entries.${index}.totalAmountLcy`,
        round(
          sumBy(
            initialBreakdown,
            (breakdown) =>
              (breakdown.fundsCalled?.fromCommitmentLcy ?? 0) +
              (breakdown.fundsCalled?.fromRecallableDistributionLcy ?? 0),
          ),
          2,
        ),
      );
    }
  }, [baseAllocations, capitalBreakdown?.length, index, setValue]);

  const handleAllocationsChange = (
    allocations: Array<{
      commitmentId: string;
      fundsCalled?: {
        fromCommitmentLcy: number;
        fromRecallableDistributionLcy: number;
      };
    }>,
  ) => {
    const breakdown = allocations.map((allocation) => ({
      commitmentId: allocation.commitmentId,
      fundsCalled: {
        fromCommitmentLcy: allocation.fundsCalled?.fromCommitmentLcy ?? 0,
        fromRecallableDistributionLcy:
          allocation.fundsCalled?.fromRecallableDistributionLcy ?? 0,
      },
    }));

    setValue(`entries.${index}.capitalBreakdown`, breakdown);
    setValue(
      `entries.${index}.totalAmountLcy`,
      round(
        sumBy(
          breakdown,
          (breakdown) =>
            (breakdown.fundsCalled?.fromCommitmentLcy ?? 0) +
            (breakdown.fundsCalled?.fromRecallableDistributionLcy ?? 0),
        ),
        2,
      ),
    );
  };

  return (
    <CapitalContributionCallAllocations
      calledFundsLabel="Capital contribution called"
      currency={baseCurrency}
      baseAllocations={baseAllocations}
      onChange={handleAllocationsChange}
      capitalBreakdown={capitalBreakdown ?? []}
    />
  );
};

export default CapitalContributionCallDetails;
